import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import HowItWorksDemo from "../components/HowItWorksDemo";
import BrandLogo from "../components/BrandLogo";
import api from "../lib/api";
import {
    FileText,
    ChartLineUp,
    Handshake,
    ShieldCheck,
    ArrowRight,
} from "@phosphor-icons/react";

const heroImg = "https://images.pexels.com/photos/18800734/pexels-photo-18800734.jpeg";
const trustImg = "https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg";

const features = [
    { icon: FileText, title: "AI Dispute Letters", desc: "Generate FDCPA & FCRA-compliant validation letters in seconds — backed by Claude Sonnet 4.5." },
    { icon: Handshake, title: "Settlement Strategy", desc: "Opening offers, counter-tiers and walk-away ceilings calibrated to your case leverage." },
    { icon: ChartLineUp, title: "Case Tracker", desc: "Dense, professional dashboard with deadlines, status, jurisdiction and documents." },
    { icon: ShieldCheck, title: "Payment Plans", desc: "Amortize settlements with interest-aware monthly schedules. Print and share." },
];

export default function Landing() {
    const [params] = useSearchParams();
    // Capture ?ref=CODE arriving at the marketing root and persist for the funnel
    useEffect(() => {
        const code = (params.get("ref") || "").trim();
        if (!code) return;
        localStorage.setItem("ll_ref", code);
        api.post("/referrals/track", { code }).catch(() => {});
    }, [params]);

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans">
            {/* Header */}
            <header className="border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2" data-testid="brand-logo-wrap">
                        <BrandLogo height={36} />
                    </div>
                    <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
                        <a href="#features" className="hover:text-slate-900">Features</a>
                        <a href="#how" className="hover:text-slate-900">How it works</a>
                        <a href="#pricing" className="hover:text-slate-900">Pricing</a>
                        <a
                            href="https://lienliftdirect.com/register?ref=kfGaMdVr"
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-slate-900"
                            data-testid="nav-affiliates"
                        >
                            Affiliates
                        </a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <a
                            href="https://lienliftdirect.com/register?ref=kfGaMdVr"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="cta-affiliates"
                        >
                            <Button variant="outline" className="hidden sm:inline-flex h-9 border-[#0F1E3D]/30 text-[#0F1E3D] hover:bg-[#0F1E3D]/5">
                                Affiliates
                            </Button>
                        </a>
                        <Link to="/login" data-testid="cta-signin" className="text-sm text-slate-700 hover:text-slate-900">Sign in</Link>
                        <Link to="/register" data-testid="cta-getstarted">
                            <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] rounded-md h-9">Get Started</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 pt-16 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 animate-fade-up">
                        <p className="overline mb-6 !text-black" data-testid="hero-eyebrow">Judgment Information &amp; Document Service · Not a Law Firm</p>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tighter font-medium">
                            We do the research.<br/>
                            We draft the letters.<br/>
                            <span className="italic text-[#2563EB]">You sign &amp; settle.</span>
                        </h1>
                        <p className="mt-8 text-lg text-black max-w-xl leading-relaxed">
                            LienLift Direct pulls records, calculates judgment interest, drafts non-legal correspondence at your direction,
                            and teaches you to negotiate. You make the calls, send the offers, and sign every agreement.
                        </p>
                        <div className="mt-10 flex gap-3 items-center">
                            <Link to="/register" data-testid="hero-cta-primary">
                                <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-12 px-6 rounded-md text-base">
                                    Open a Case Free <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/login" data-testid="hero-cta-secondary"
                                className="text-slate-700 hover:text-slate-900 underline-offset-4 hover:underline text-base">
                                I already have an account
                            </Link>
                        </div>
                        <div className="mt-8">
                            <Link to="/coverage" className="inline-flex items-center gap-2 text-xs text-[#0F1E3D] bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition-colors" data-testid="hero-coverage-badge">
                                <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <strong>Live in 14 states</strong>
                                <span className="text-slate-500">·</span>
                                <span>Federal RECAP nationwide</span>
                                <ArrowRight size={12} className="ml-1" />
                            </Link>
                        </div>
                        <div className="mt-6 flex items-center gap-6 text-xs text-black font-medium">
                            <span>● 14 states + federal</span>
                            <span>● No legal advice given</span>
                            <span>● Built on Claude Sonnet 4.5</span>
                        </div>
                    </div>
                    <div className="md:col-span-5 relative">
                        <HowItWorksDemo />
                    </div>
                </div>
            </section>

            {/* Trusted by marquee */}
            <section className="border-y border-slate-200 bg-slate-50 overflow-hidden">
                <div className="max-w-[1400px] mx-auto py-8 flex items-center gap-6">
                    <span className="overline !text-black pl-6 shrink-0">Used by</span>
                    <div className="marquee flex-1 overflow-hidden">
                        <div className="marquee-track flex items-center gap-12 text-black text-sm font-medium whitespace-nowrap">
                            {Array.from({ length: 2 }).map((_, dup) => (
                                <div key={dup} className="flex items-center gap-12 shrink-0">
                                    <span>Solo Attorneys</span>
                                    <span>Collection Agencies</span>
                                    <span>Consumer Advocates</span>
                                    <span>Pro-se Debtors</span>
                                    <span>Settlement Negotiators</span>
                                    <span>Judgment Information Services</span>
                                    <span>Paralegal Teams</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="max-w-[1400px] mx-auto px-6 py-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-12">
                    <div className="md:col-span-7">
                        <p className="overline">Capabilities</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">A workbench, not a chatbot.</h2>
                    </div>
                    <p className="md:col-span-5 text-slate-600">
                        Every feature is grounded in a single case record. Documents, AI outputs and payment math stay
                        synced — so your strategy is the artefact, not a thread.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0F1E3D] border border-[#0F1E3D] rounded-md overflow-hidden">
                    {features.map((f) => {
                        const Icon = f.icon;
                        return (
                            <div key={f.title} className="bg-[#0F1E3D] p-8 hover:bg-[#1A2C57] transition-colors">
                                <Icon size={28} weight="duotone" className="text-white" />
                                <h3 className="text-lg font-semibold mt-6 text-white">{f.title}</h3>
                                <p className="text-sm text-slate-300 mt-2 leading-relaxed">{f.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <img src={trustImg} alt="settlement handshake" className="w-full aspect-square object-cover rounded-md border border-slate-200" />
                    </div>
                    <div className="md:col-span-7">
                        <p className="overline">Workflow</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">From notice to settlement in four moves.</h2>
                        <ol className="mt-10 space-y-6">
                            {[
                                ["Open the case", "Add the creditor, amount, jurisdiction, court record and any documents."],
                                ["We prepare the documents", "Validation, dispute or settlement letters drafted at your direction — you sign and send."],
                                ["Practice the negotiation", "Role-play with the AI creditor; get coach feedback before the real call."],
                                ["Run the math", "State-statutory judgment interest, payment plan amortization, and a settlement worksheet."],
                            ].map(([t, d], i) => (
                                <li key={t} className="flex gap-5">
                                    <span className="font-serif text-3xl text-[#2563EB] leading-none w-10 shrink-0">{String(i+1).padStart(2,"0")}</span>
                                    <div>
                                        <p className="font-semibold">{t}</p>
                                        <p className="text-sm text-slate-600 mt-1">{d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="pricing" className="max-w-[1400px] mx-auto px-6 py-24 text-center">
                <h2 className="font-serif text-5xl sm:text-6xl mt-4 leading-tight">Your first case is on us.</h2>
                <p className="mt-6 text-slate-600 max-w-xl mx-auto">
                    Register, open a case, and run the full pipeline — dispute letter, settlement strategy and amortized payment plan.
                </p>
                <div className="mt-10">
                    <Link to="/register" data-testid="footer-cta">
                        <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-12 px-8 rounded-md text-base">
                            Create your account <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>

            <footer className="border-t border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
                    <p>© {new Date().getFullYear()} LienLift Direct. Not a law firm. Not legal advice.</p>
                    <p>Built for negotiation. Powered by Claude Sonnet 4.5.</p>
                </div>
            </footer>
        </div>
    );
}
